import Dexie, { type Table } from "dexie";
import { resetTransientChatCache } from "@/domains/offline/chat/chatCache";
import { resetTransientChatOutbox } from "@/domains/offline/chat/chatOutboxStore";
import type {
  AuthProfileRecord,
  ChatAvailabilityRecord,
  ChatOutboxEntry,
  ConversationMessagesRecord,
  ConversationRecord,
  IdMappingRecord,
  ListCacheRecord,
  OutboxEntry,
  PatientBundleRecord,
  PatientListRow,
  SessionRow,
  SyncMetaRecord,
} from "@/domains/offline/db/schema";

export class MivoOfflineDb extends Dexie {
  patients!: Table<PatientListRow, string>;
  patientBundles!: Table<PatientBundleRecord, string>;
  sessions!: Table<SessionRow, string>;
  authProfile!: Table<AuthProfileRecord, string>;
  outbox!: Table<OutboxEntry, string>;
  idMappings!: Table<IdMappingRecord, string>;
  syncMeta!: Table<SyncMetaRecord, string>;
  conversations!: Table<ConversationRecord, string>;
  conversationMessages!: Table<ConversationMessagesRecord, string>;
  chatOutbox!: Table<ChatOutboxEntry, string>;
  chatAvailability!: Table<ChatAvailabilityRecord, string>;
  listCaches!: Table<ListCacheRecord, string>;

  constructor() {
    super("mivo-offline");
    this.version(1).stores({
      patients: "id",
      patientBundles: "patientId",
      sessions: "id",
      authProfile: "id",
      outbox: "id, status, createdAt",
      idMappings: "tempId, serverId",
      syncMeta: "key",
    });
    this.version(2).stores({
      patients: "id",
      patientBundles: "patientId",
      sessions: "id",
      authProfile: "id",
      outbox: "id, status, createdAt",
      idMappings: "tempId, serverId",
      syncMeta: "key",
      conversations: "id",
      conversationMessages: "conversationId",
      chatOutbox: "id, status, createdAt, conversationId",
      chatAvailability: "therapistId",
      listCaches: "key",
    });
    this.version(3)
      .stores({
        patients: "id",
        patientBundles: "patientId",
        sessions: "id",
        authProfile: "id",
        outbox: "id, status, createdAt",
        idMappings: "tempId, serverId",
        syncMeta: "key",
        conversations: "id",
        conversationMessages: "conversationId",
        chatOutbox: "id, status, createdAt, conversationId",
        chatAvailability: "therapistId",
        listCaches: "key",
      })
      .upgrade(async (tx) => {
        await Promise.all([
          tx.table("patientBundles").clear(),
          tx.table("outbox").clear(),
          tx.table("idMappings").clear(),
          tx.table("conversations").clear(),
          tx.table("conversationMessages").clear(),
          tx.table("chatOutbox").clear(),
          tx.table("chatAvailability").clear(),
          tx.table("listCaches").clear(),
        ]);
      });
  }
}

export const mivoDb = new MivoOfflineDb();

export async function clearOfflineData(): Promise<void> {
  resetTransientChatCache();
  resetTransientChatOutbox();

  await Promise.all([
    mivoDb.patients.clear(),
    mivoDb.patientBundles.clear(),
    mivoDb.sessions.clear(),
    mivoDb.authProfile.clear(),
    mivoDb.outbox.clear(),
    mivoDb.idMappings.clear(),
    mivoDb.syncMeta.clear(),
    mivoDb.conversations.clear(),
    mivoDb.conversationMessages.clear(),
    mivoDb.chatOutbox.clear(),
    mivoDb.chatAvailability.clear(),
    mivoDb.listCaches.clear(),
  ]);
}
